import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent
} from "@/components/ui/card";
import { LandingHeader } from "@/components/LandingHeader";
import { Footer } from "@/components/Footer";
import { trackLoginInitiated } from "@/utils/eventTracking";

/** Prefer `?redirect=`; else location from Navigate state (pathname + search). */
function resolvePostLoginPath(
  search: string,
  state: { from?: { pathname: string; search: string } } | null
): string {
  const redirectParam = new URLSearchParams(search).get("redirect");
  if (redirectParam) {
    return redirectParam.startsWith("/") ? redirectParam : `/${redirectParam}`;
  }
  const from = state?.from;
  if (from) {
    return `${from.pathname}${from.search || ""}`;
  }
  return "/";
}

function navigateToInternalPath(
  navigate: ReturnType<typeof useNavigate>,
  path: string
) {
  if (!path.startsWith("/") || path.startsWith("//")) {
    navigate("/", { replace: true });
    return;
  }
  try {
    const url = new URL(path, window.location.origin);
    navigate(
      {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
      },
      { replace: true }
    );
  } catch {
    navigate(path, { replace: true });
  }
}

export const LoginPageCustom = () => {
  const { login, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);

  const handleCredentialResponse = async (response: any) => {
    try {
      trackLoginInitiated('google', document.referrer && document.referrer.trim() !== '' ? document.referrer : 'direct', navigator.userAgent);
      
      await login(response);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  useEffect(() => {
    if (!token) return;

    const timer = window.setTimeout(() => {
      const target = resolvePostLoginPath(location.search, location.state);
      navigateToInternalPath(navigate, target);
    }, 100);

    return () => clearTimeout(timer);
  }, [token, navigate, location.search, location.state]);

  useEffect(() => {
    // Function to check if Google script is loaded
    const checkGoogleScriptLoaded = () => {
      if (window.google?.accounts?.id) {
        setIsGoogleScriptLoaded(true);
      } else {
        // Check again in 100ms if not loaded
        setTimeout(checkGoogleScriptLoaded, 100);
      }
    };

    checkGoogleScriptLoaded();
  }, []);

  useEffect(() => {
    if (!isGoogleScriptLoaded) return;
    const g_client_id = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    try {
      window.google.accounts.id.initialize({
        client_id: g_client_id,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      });

      // Add a small delay to ensure DOM element is ready
      setTimeout(() => {
        const buttonContainer = document.getElementById("googleSignInDiv");
        if (buttonContainer) {
          window.google.accounts.id.renderButton(
            buttonContainer,
            {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "signin_with",
              shape: "rectangular",
              logo_alignment: "center",
            }
          );
        }
      }, 100);
    } catch (error) {
      console.error("Google Sign In initialization error:", error);
    }
  }, [isGoogleScriptLoaded]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-900">
      <LandingHeader showLoginButton={false} />

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-md mx-auto text-center">
          {/* Top Section: Icon and Welcome */}
          <div className="mb-8">
            {/* Circular Icon */}
            <div className="w-20 h-20 bg-purple-600/20 p-4 rounded-full flex items-center justify-center mx-auto mb-6">
              <img src="../assets/instavault-icon.svg"/>
            </div>
            
            {/* Welcome Title */}
            <h1 className="text-3xl font-bold text-white mb-4">
              Welcome to Instavault
            </h1>
            
            {/* Description */}
            <p className="text-lg text-slate-300 mb-8">
              Transform your social saves into organized, searchable knowledge. Sign in with Google to get started in seconds.
            </p>
          </div>

          {/* Sign-in Card */}
          <Card className="w-full bg-slate-800/50 border-slate-700 mb-8">
            <CardContent className="p-8">
              {/* Card Title */}
              <h2 className="text-2xl font-bold text-white mb-2">
                Get Started
              </h2>
              
              {/* Card Subtitle */}
              <p className="text-slate-300 mb-6">
                One click to unlock your all social media saves
              </p>
              
              {/* Google Sign-In Button */}
              <div className="mb-6">
                {!isGoogleScriptLoaded && (
                  <div className="animate-pulse bg-slate-600 h-12 w-64 rounded mx-auto"></div>
                )}
                <div
                  id="googleSignInDiv"
                  className="flex justify-center"
                ></div>
              </div>
              
              {/* Legal Text */}
              <p className="text-sm text-slate-400">
                By signing in, you agree to our {" "}
                <a href={`${import.meta.env.VITE_LANDING_PAGE_URL}/terms`} className="text-purple-400 hover:text-purple-300 underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href={`${import.meta.env.VITE_LANDING_PAGE_URL}/privacy`} className="text-purple-400 hover:text-purple-300 underline">
                  Privacy Policy
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Bottom Features */}
          <div className="flex justify-center space-x-8 text-sm text-slate-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Secure</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
              </svg>
              <span>10k+ Users</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <span>Instant Setup</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};